import React, { FC } from 'react';
import cls from 'classnames';
import { ReactComponent as KiwiGlasses } from '../../assets/svg/icons/kiwi-glasses-two-fingers.svg';
import { ReactComponent as KiwiSad } from '../../assets/svg/stickers/Mascot/Mascot_canceled.svg';
import { IconComp } from '../SwapKiwi/IconComponent';
import { ButtonSizes, SKButton } from '../SwapKiwi/Button';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../constants/routes';
import { ReactComponent as Link } from '../../assets/svg/icons/link-external-01.svg';
import { useSelector } from 'react-redux';
import { getTransactions } from '../../ducks/transactions/selectors';
import { useEtherscanHash } from '../../hooks/contract/useEtherscan';

export type SwapResolvedType = 'success' | 'rejected';
export type SwapResolvedAnnotations = 'toYourNFTs' | 'toOpenSwaps';

export interface ISwapResolvedMessage {
  type: SwapResolvedType;
  message: string;
  annotations?: SwapResolvedAnnotations;
}

export const SwapResolvedMessage: FC<ISwapResolvedMessage> = ({ type, message }) => {
  const history = useHistory();
  const etherscanLink = useEtherscanHash();
  const transactions = useSelector(getTransactions);
  const createdMessage =
    'Congrats - you are almost there. Now your counterpart needs to add their NFTs to your swap proposal.<br />Be aware that some scammers might try to convince you that there is something wrong with the swap and they want to change platforms.<br /> Always be vigilant. If something seems too good to be true - don’t do it. Feel free to reach out to us if something is not right.<br />Have a look at the swap you started.';
  const rejectMessage =
    'Done. Your swap has been cancelled. Your NFTs are transferred back to your wallet. It might be that our frontend takes a couple of seconds to fetch the info to display it, but you can always confirm it on the blockchain. Hit the “Etherscan” button and check it out. You will also find the cancelled swap on recent swaps.';
  const initiatedMessage =
    'Congrats - you are almost there. Now your counterpart needs to accept the swap. They can either confirm or reject the swap but they can’t change anything anymore. This last step is just to confirm that there is everything in what you agreed on. Feel free to reach out to us if something is not right.Have a look at the swap you answered.';
  const swapFinished =
    'Congrats. You did it. Enjoy your new assets. You can now find the information around the swap in recent swaps. It might be that our frontend takes a couple of seconds to fetch the info and transfer it to recent swaps, but you can always confirm it on the blockchain. Hit the “Etherscan” button and check it out. And if you feel happy about your experience feel free to share it on Twitter.';

  const getSwapMessage = (): string => {
    switch (true) {
      case message.includes('created'):
        return createdMessage;
      case message.includes('initiated'):
        return initiatedMessage;
      case message.includes('successful'):
        return swapFinished;
      default:
        return rejectMessage;
    }
  };

  return (
    <div
      className={cls('flex justify-center items-center min-h-46.25 border-t border-cloud-900', {
        'bg-kiwi-400': type === 'success',
        'bg-lemon-400': type === 'rejected',
      })}
    >
      <div className="flex flex-col items-center max-w-33.75 text-center">
        <IconComp icon={type === 'success' ? KiwiGlasses : KiwiSad} size={470} className="-mb-4" />
        <h4 className="text-display-lg text-cloud-900 mb-2">{message}</h4>
        <p
          className="text-lg text-cloud-900 mb-3 text-center"
          dangerouslySetInnerHTML={{
            __html: getSwapMessage(),
          }}
        ></p>
        <div className="w-full flex flex-col gap-y-1 items-center mb-9">
          {(message.includes('initiated') || message.includes('created')) && (
            <SKButton
              className="w-full"
              color="secondary"
              size={ButtonSizes.XL}
              onClick={(): void => history.push(Routes.OPEN_SWAPS)}
            >
              <p className="text-base text-cloud-900">Check Open Swaps</p>
            </SKButton>
          )}
          {(message.includes('successful') || message.includes('cancel')) && (
            <SKButton
              className="w-full"
              color="secondary"
              size={ButtonSizes.XL}
              onClick={(): void => history.push(Routes.RECENT_SWAPS)}
            >
              <p className="text-base text-cloud-900">Check Recent Swaps</p>
            </SKButton>
          )}
          {message.includes('successful') && (
            <SKButton className="w-full" color="secondary" size={ButtonSizes.XL}>
              <a
                href={`https://twitter.com/intent/tweet?text=Checkout%20my%20swap%20at%20https://swap.kiwi%20%0A%0AEtherscan:%20${etherscanLink}/${transactions[0]?.hash}`}
                className="text-base text-cloud-900"
                target="_blank"
                rel="noreferrer"
              >
                Share on Twitter
              </a>
            </SKButton>
          )}
          {transactions[0]?.hash && (
            <a
              href={`${etherscanLink}/${transactions[0]?.hash}`}
              target="_blank"
              rel="noreferrer"
              className="px-52.5 py-1.5 gap-x-1 text-base bg-white focus:bg-cloud-200 border-cloud-900 border sk-cta rounded-xl text-black flex items-center justify-center transition ease-in-out duration-100 w-full"
            >
              <span className="flex items-center">
                Etherscan
                <IconComp icon={Link} className="ml-1" size={16} />
              </span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
